import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { isOffline } from "@/lib/offline/is-offline";
import { readAppShellSnapshot } from "@/lib/offline/app-shell-snapshot";

/**
 * Hook para verificar si el usuario tiene empresa asignada
 * Retorna el contexto del usuario o null si no tiene empresa
 *
 * Resiliencia offline (warm): si la query de Convex está `undefined` (no
 * resolvió) y estamos sin conexión, caemos al snapshot local del app-shell
 * (ver lib/offline/app-shell-snapshot.ts) para que la app siga navegable.
 */
export function useCompanyContext() {
  const liveContext = useQuery(api.system.context.queries.checkUserContext);

  // Solo usamos el cache cuando la query no resolvió Y estamos offline. Online
  // (incluido el valor cacheado en memoria por Convex tras un blip) mandamos la
  // query viva. `null` (sesión sin empresa) es un valor válido, no un fallback.
  const useSnapshot = liveContext === undefined && isOffline();
  const snapshotContext = useMemo(
    () =>
      useSnapshot ? (readAppShellSnapshot()?.context ?? undefined) : undefined,
    [useSnapshot],
  );

  const userContext = liveContext === undefined ? snapshotContext : liveContext;
  const fromCache = liveContext === undefined && snapshotContext !== undefined;

  // Type guard para verificar si es un error
  const isError =
    userContext &&
    typeof userContext === "object" &&
    "error" in userContext &&
    "type" in userContext;

  // Type guard para verificar si es UserContext válido
  const isValidContext =
    userContext &&
    typeof userContext === "object" &&
    "userId" in userContext &&
    "companyId" in userContext;

  return {
    userContext,
    isLoading: userContext === undefined,
    /** true cuando el contexto proviene del snapshot offline, no de la query. */
    fromCache,
    hasSubscription: isValidContext ? userContext.subscription : undefined,
    hasCompany: isValidContext ? !!userContext.companyId : false,
    companyId: isValidContext ? userContext.companyId : undefined,
    userId: isValidContext ? userContext.userId : undefined,
    role: isValidContext ? userContext.role : undefined,
    branchId: isValidContext ? userContext.branchId : undefined,
    activeBranchId: isValidContext ? userContext.activeBranchId : undefined,
    availableBranches: isValidContext ? userContext.availableBranches : [],
    isError,
  };
}
